import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

type Props = {
  translationId: string;
  translation: string;
  override?: string;
  locale: string;
  isOverridable: boolean;
  canSaveOriginalAsOverride: boolean;
  refetch: () => void;
  requiresManualTranslation?: boolean;
};

export const TranslationLine = ({
  translationId,
  translation,
  override,
  locale,
  isOverridable,
  canSaveOriginalAsOverride,
  refetch,
  requiresManualTranslation = false,
}: Props) => {
  const { authFetch } = useAuth({ scopes: [] });

  const [text, setText] = useState(override ?? translation);
  const [isOverridden, setIsOverridden] = useState(!!override);
  const [isChanged, setIsChanged] = useState(false);
  const hasOverride = override !== undefined;
  const showManualTranslationWarning = requiresManualTranslation && !hasOverride;

  useEffect(() => {
    setIsOverridden(text !== translation);

    if (override) {
      text === override ? setIsChanged(false) : setIsChanged(true);
    }
    if (!override) {
      text === translation ? setIsChanged(false) : setIsChanged(true);
    }
  }, [text, translation, override]);

  const { mutate } = useMutation({
    mutationFn: async () => {
      const response = await authFetch(`/api/overrides/${translationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, locale }),
      });
      return await response.json();
    },
    onSuccess: () => {
      refetch();
    },
  });

  return (
    <form
      className={`translation-line${showManualTranslationWarning ? ' requires-manual-translation' : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        mutate();
      }}
    >
      <input className="locale-label" type="text" value={locale} disabled />
      {hasOverride ? (
        <span className="override-icon" title="Showing overridden value">
          ↻
        </span>
      ) : showManualTranslationWarning ? (
        <span
          className="requires-manual-translation-icon"
          title="Requires manual translation"
        >
          ⚠
        </span>
      ) : null}
      <input
        className="override-toggle"
        disabled={!isOverridable}
        type="checkbox"
        checked={isOverridden}
        onChange={() => {
          if (isOverridden) {
            setText(translation);
            setIsOverridden(false);
          }
          if (!isOverridden) {
            setText(override ?? '');
            setIsOverridden(true);
          }
        }}
      />
      <textarea
        className="translation-text"
        value={text}
        disabled={!isOverridable}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="submit"
        disabled={(!isChanged && !canSaveOriginalAsOverride) || !isOverridable}
      >
        Save
      </button>
    </form>
  );
};
